import { Card, List, ListItemButton, ListItemSecondaryAction, ListItemText } from '@mui/material';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { API } from '~/api';
import { BrandService } from '~/service/service.brand';
import { BrandId } from '~/shared/dto/id';
import { useService } from '~/shared/service/service.base';
import { GridNoRows } from '~/view/grid/grid.norows';
import { AsyncView } from '~/view/view.async';
import { Col } from '~/view/view.box';

export const BrandSuggestions = observer<{ brandId: BrandId }>(({ brandId }) => {
	const [svc] = useService(BrandService);
	svc.suggestions.use(brandId);

	return (
		<Col>
			<List sx={{ height: '70vh' }}>
				<Virtuoso
					data={svc.suggestions.data.slice()}
					endReached={svc.suggestions.fetchNextPage}
					components={{
						EmptyPlaceholder: () => <GridNoRows>No suggestions</GridNoRows>,
						Footer: () => <AsyncView async={svc.suggestions.async} />,
					}}
					itemContent={(idx, data) => <BrandSuggestionsItem index={idx} item={data} />}
				/>
			</List>
		</Col>
	);
});

const BrandSuggestionsItem = observer<{ index: number; item: API.Feedback }>(({ item }) => {
	const [svc] = useService(BrandService);

	const handleClick = useCallback(() => svc.suggestions.drawer.setSelectedItem(item), [item, svc.suggestions.drawer]);

	const selected = svc.suggestions.drawer.selectedItem?.id === item.id;
	const color = useMemo(
		() => (item.status === 'NEW' ? 'primary.main' : item.status === 'DONE' ? 'success.main' : 'error.main'),
		[item.status],
	);

	return (
		<ListItemButton
			key={item.id}
			elevation={1}
			sx={{ mb: 1, borderRadius: 1 }}
			component={Card}
			onClick={handleClick}
			selected={selected}
		>
			<ListItemText sx={{ pr: 14 }} primary={`${item.createdByName}`} secondary={item.message} />
			<ListItemSecondaryAction>
				<ListItemText
					primary={item.status}
					secondary={dayjs(item.updatedAt).format('YYYY-MM-DD HH:mm')}
					primaryTypographyProps={{ textAlign: 'end', fontWeight: 500, color }}
				/>
			</ListItemSecondaryAction>
		</ListItemButton>
	);
});
